import React, { useEffect, useState } from "react";
import { Plus, Route, FileDown, Pencil, Trash2, Truck, User, MapPin, Users, Loader2, GripVertical, X } from "lucide-react";
import { Reorder } from "framer-motion";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Card } from "@/components/Cards";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const today = () => new Date().toISOString().slice(0, 10);
const dLabel = (d) => new Date(`${d}T00:00:00`).toLocaleDateString("tr-TR", { day: "numeric", month: "long", year: "numeric", weekday: "long" });

export default function Shipment() {
  const [date, setDate] = useState(today());
  const [data, setData] = useState({ vehicles: [], unassigned: [] });
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);

  const load = async (d = date) => {
    setLoading(true);
    try {
      const [s, c] = await Promise.all([
        api.get("/shipment/vehicles", { params: { date: d } }),
        api.get("/customers"),
      ]);
      setData(s.data);
      setCustomers(c.data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(date); /* eslint-disable-next-line */ }, [date]);

  const del = async (v) => {
    if (!window.confirm(`"${v.name}" aracı silinsin mi?`)) return;
    try { await api.delete(`/shipment/vehicles/${v.id}`); toast.success("Araç silindi"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-6" data-testid="shipment-page">
      <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight">Sevkiyat</h1>
          <p className="text-sm text-muted-foreground mt-1">Araç ve rotaları tanımlayın, her araca müşterileri atayın. Kişi sayıları seçilen günün Günlük Kişi Girişi'nden otomatik gelir.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="flex items-center gap-2">
            <Label htmlFor="ship-date" className="text-xs text-muted-foreground whitespace-nowrap">Sevkiyat günü</Label>
            <Input id="ship-date" type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="shipment-date-input" className="h-11 w-[160px] rounded-full" />
          </div>
          <a href={`${BACKEND}/api/shipment/pdf?date=${date}`} target="_blank" rel="noreferrer" data-testid="shipment-pdf-all">
            <Button variant="outline" className="rounded-full h-11 gap-2"><FileDown className="h-4 w-4" /> Tümü PDF</Button>
          </a>
          <Button onClick={() => setEditing({ name: "", driver: "", route: "", customer_ids: [] })} data-testid="shipment-add-btn" className="rounded-full h-11 px-6 font-semibold gap-2">
            <Plus className="h-4 w-4" /> Yeni Araç
          </Button>
        </div>
      </div>

      <div className="text-sm font-medium text-muted-foreground" data-testid="shipment-date-label">{dLabel(date)}</div>

      {loading ? (
        <div className="flex items-center justify-center py-20 text-muted-foreground"><Loader2 className="h-6 w-6 animate-spin" /></div>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="shipment-vehicles">
            {data.vehicles.length === 0 && (
              <Card className="p-8 text-center text-muted-foreground md:col-span-2 xl:col-span-3" data-testid="shipment-empty">
                Henüz araç yok — "Yeni Araç" ile rota ve araç tanımlayın.
              </Card>
            )}
            {data.vehicles.map((v) => (
              <Card key={v.id} className="p-4 flex flex-col" data-testid={`shipment-vehicle-${v.id}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="h-10 w-10 rounded-lg bg-primary/10 text-primary flex items-center justify-center shrink-0"><Truck className="h-5 w-5" /></div>
                    <div className="min-w-0">
                      <div className="font-heading font-semibold truncate">{v.name}</div>
                      <div className="text-xs text-muted-foreground flex flex-wrap gap-x-3 gap-y-0.5 mt-0.5">
                        {v.route && <span className="inline-flex items-center gap-1"><MapPin className="h-3 w-3" /> {v.route}</span>}
                        {v.driver && <span className="inline-flex items-center gap-1"><User className="h-3 w-3" /> {v.driver}</span>}
                      </div>
                    </div>
                  </div>
                  <div className="flex gap-1 shrink-0">
                    <a href={`${BACKEND}/api/shipment/vehicles/${v.id}/pdf?date=${date}`} target="_blank" rel="noreferrer" data-testid={`shipment-pdf-${v.id}`} title="Şoför föyü PDF" className="h-8 w-8 rounded-lg hover:bg-accent flex items-center justify-center text-muted-foreground hover:text-foreground"><FileDown className="h-4 w-4" /></a>
                    <button onClick={() => setEditing(v)} data-testid={`shipment-edit-${v.id}`} title="Düzenle" className="h-8 w-8 rounded-lg hover:bg-accent flex items-center justify-center text-muted-foreground hover:text-foreground"><Pencil className="h-4 w-4" /></button>
                    <button onClick={() => del(v)} data-testid={`shipment-delete-${v.id}`} title="Sil" className="h-8 w-8 rounded-lg hover:bg-destructive/10 flex items-center justify-center text-muted-foreground hover:text-destructive"><Trash2 className="h-4 w-4" /></button>
                  </div>
                </div>
                <div className="mt-3 border-t border-border pt-2 flex-1" data-testid={`shipment-customers-${v.id}`}>
                  {v.customers.length === 0 && <div className="text-xs text-muted-foreground py-2">Müşteri atanmadı</div>}
                  {v.customers.map((c) => (
                    <div key={c.id} className="flex items-center justify-between py-1 text-sm">
                      <span className="truncate pr-2">{c.name}</span>
                      <span className="font-semibold tabular-nums shrink-0">{c.headcount}</span>
                    </div>
                  ))}
                </div>
                <div className="mt-2 border-t border-border pt-2 flex items-center justify-between text-sm font-semibold">
                  <span className="inline-flex items-center gap-1.5 text-muted-foreground"><Users className="h-3.5 w-3.5" /> Toplam</span>
                  <span className="tabular-nums" data-testid={`shipment-total-${v.id}`}>{v.total_headcount}</span>
                </div>
              </Card>
            ))}
          </div>

          {data.unassigned.length > 0 && (
            <Card className="p-4 border-amber-500/40 bg-amber-500/5" data-testid="shipment-unassigned">
              <div className="flex items-center gap-2 text-amber-600 font-heading font-semibold text-sm"><Route className="h-4 w-4" /> Henüz araca atanmamış müşteriler</div>
              <p className="text-xs text-muted-foreground mt-0.5">Bu gün için kaydı olan ancak hiçbir araca atanmamış müşteriler. (Sayı = seçili günün kişi girişi)</p>
              <div className="mt-3 flex flex-wrap gap-2">
                {data.unassigned.map((c) => (
                  <span key={c.id} className="rounded-full bg-background border border-border px-3 py-1 text-xs" data-testid={`shipment-unassigned-${c.id}`}>
                    {c.name} {c.headcount > 0 && <b className="ml-1">{c.headcount}</b>}
                  </span>
                ))}
              </div>
            </Card>
          )}
        </>
      )}

      {editing && <VehicleDialog vehicle={editing} customers={customers} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </div>
  );
}

const VehicleDialog = ({ vehicle, customers, onClose, onSaved }) => {
  const [form, setForm] = useState({
    name: vehicle.name || "",
    driver: vehicle.driver || "",
    route: vehicle.route || "",
    customer_ids: vehicle.customer_ids || (vehicle.customers || []).map((c) => c.id),
  });
  const [saving, setSaving] = useState(false);
  const isEdit = !!vehicle.id;

  const custMap = React.useMemo(() => Object.fromEntries(customers.map((c) => [c.id, c])), [customers]);
  const available = customers.filter((c) => !form.customer_ids.includes(c.id));
  const toggle = (id) => setForm((f) => ({ ...f, customer_ids: f.customer_ids.includes(id) ? f.customer_ids.filter((x) => x !== id) : [...f.customer_ids, id] }));

  const save = async () => {
    if (!form.name.trim()) { toast.error("Araç adı / plaka gerekli"); return; }
    setSaving(true);
    try {
      if (isEdit) await api.put(`/shipment/vehicles/${vehicle.id}`, form);
      else await api.post("/shipment/vehicles", form);
      toast.success(isEdit ? "Araç güncellendi" : "Araç eklendi");
      onSaved();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="shipment-dialog">
        <DialogHeader><DialogTitle>{isEdit ? "Aracı Düzenle" : "Yeni Araç"}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div>
            <Label htmlFor="v-name">Araç adı / Plaka</Label>
            <Input id="v-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Örn: 06 ABC 123 — Ford Transit" data-testid="shipment-name-input" className="mt-1" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="v-driver">Şoför</Label>
              <Input id="v-driver" value={form.driver} onChange={(e) => setForm({ ...form, driver: e.target.value })} placeholder="Şoför adı" data-testid="shipment-driver-input" className="mt-1" />
            </div>
            <div>
              <Label htmlFor="v-route">Rota</Label>
              <Input id="v-route" value={form.route} onChange={(e) => setForm({ ...form, route: e.target.value })} placeholder="Örn: Çankaya Hattı" data-testid="shipment-route-input" className="mt-1" />
            </div>
          </div>
          <div>
            <Label>Teslim sırası <span className="text-muted-foreground font-normal">(sürükleyerek değiştirin)</span></Label>
            {form.customer_ids.length === 0 ? (
              <p className="text-xs text-muted-foreground mt-1">Aşağıdan müşteri ekleyin — föy bu sırayla yazdırılır.</p>
            ) : (
              <Reorder.Group axis="y" values={form.customer_ids} onReorder={(v) => setForm((f) => ({ ...f, customer_ids: v }))} className="mt-2 space-y-1.5" data-testid="shipment-order-list">
                {form.customer_ids.map((id, idx) => {
                  const c = custMap[id];
                  if (!c) return null;
                  return (
                    <Reorder.Item key={id} value={id} className="flex items-center gap-2 rounded-lg border border-border bg-background px-2.5 py-2 cursor-grab active:cursor-grabbing select-none" data-testid={`shipment-order-${id}`}>
                      <GripVertical className="h-4 w-4 text-muted-foreground shrink-0" />
                      <span className="text-xs font-mono text-muted-foreground w-5 shrink-0">{idx + 1}.</span>
                      <span className="text-sm flex-1 truncate">{c.short_name || c.name}</span>
                      <button type="button" onClick={() => toggle(id)} className="h-6 w-6 rounded hover:bg-destructive/10 flex items-center justify-center text-muted-foreground hover:text-destructive shrink-0" data-testid={`shipment-remove-${id}`}><X className="h-3.5 w-3.5" /></button>
                    </Reorder.Item>
                  );
                })}
              </Reorder.Group>
            )}
            <div className="mt-3">
              <Label className="text-xs text-muted-foreground">Müşteri ekle</Label>
              <div className="mt-1.5 flex flex-wrap gap-2 max-h-40 overflow-y-auto" data-testid="shipment-customer-list">
                {customers.length === 0 && <span className="text-sm text-muted-foreground">Önce müşteri ekleyin</span>}
                {customers.length > 0 && available.length === 0 && <span className="text-xs text-muted-foreground">Tüm müşteriler eklendi</span>}
                {available.map((c) => (
                  <button type="button" key={c.id} onClick={() => toggle(c.id)} className="flex items-center gap-1.5 rounded-full border border-border px-3 py-1.5 text-sm hover:border-primary hover:bg-primary/5" data-testid={`shipment-customer-${c.id}`}>
                    <Plus className="h-3 w-3" /> {c.short_name || c.name}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="rounded-full" data-testid="shipment-cancel-btn">İptal</Button>
          <Button onClick={save} disabled={saving} className="rounded-full gap-2" data-testid="shipment-save-btn">
            {saving && <Loader2 className="h-4 w-4 animate-spin" />} Kaydet
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
